import React, { useContext, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import ReorderResizeTable from 'components/ReorderResizeTable';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import {
  CUSTOM_COL_SIZES,
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  TABLE_COLUMNS,
} from './components/data';
import { BODY_CONTENT_GENERATE } from './components';
import { DealRoom } from 'models/DealRoom';
import {
  getSellingPortfolio,
  isSingleElementArray,
  getIsArrayEmpty,
} from 'utils';
import useQueryParams from 'hooks/useQueryParams';
import usePageParams from 'hooks/usePageParams';
import api from 'api';
import EnterpriseContext from '../../../../providers/Enterprise/Enterprise.context';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { OptionValue } from 'ncoded-component-library/build/components/molecules/Select/Select.component';
import useDealsFilterOptions from '../../../../hooks/useDealsFilterOptions';
import useTableFetchState from 'hooks/useTableFetchState';
import { ColumnSizing } from 'components/ReorderResizeTable/types';

import './AnalyticsDeals.styles.scss';

type AnalyticsDealsProps = {
  className?: string;
  translatedSortOptions: OptionValue<string>[];
  columns: typeof TABLE_COLUMNS;
  columnSizing: ColumnSizing;
  onChipLabelClick?: (fieldKey: string) => void;
  onSortChipLabelClick?: (fieldKey: string) => void;
  setIsEmpty?: (value: boolean) => void;
  onReorder: (columns: typeof TABLE_COLUMNS) => void;
  onColumnSizingChange: (columnSizing: ColumnSizing) => void;
};

const AnalyticsDeals: React.FC<AnalyticsDealsProps> = (props) => {
  const {
    className,
    translatedSortOptions,
    columns,
    columnSizing,
    onChipLabelClick,
    onSortChipLabelClick,
    setIsEmpty,
    onReorder,
    onColumnSizingChange,
  } = props;

  const classes = classNames('analytics-deals', className);

  const {
    isLoading,
    totalPages,
    totalItems,
    data: deals,
    setIsLoading,
    setPaginatedData,
  } = useTableFetchState<DealRoom>();

  const { isOfficeManager, enterpriseMember } = useContext(CurrentUserContext);
  const { enterprise, units } = useContext(EnterpriseContext);

  const page = usePageParams();
  const { params } = useQueryParams();
  const {
    stage,
    industry,
    colleagueIds,
    timeframe,
    hasListing,
    enterpriseBusinessUnitId: paramsEnterpriseBusinessUnitId,
    sort = SORTING_VALUES.stage,
  } = params;

  const enterpriseBusinessUnitId = useMemo(
    () =>
      isOfficeManager
        ? enterpriseMember?.enterpriseBusinessUnitId
        : paramsEnterpriseBusinessUnitId,
    [
      paramsEnterpriseBusinessUnitId,
      enterpriseMember?.enterpriseBusinessUnitId,
      isOfficeManager,
    ],
  );

  const { limit } = useNumberOfResultsPerPage();

  const {
    stageFilterOption,
    businessUnitFilterOption,
    hasListingFilterOption,
    filterOptions,
  } = useDealsFilterOptions(units);

  const navigate = useNavigate();

  const renderedContent = useMemo(
    () =>
      TABLE_COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (deal: DealRoom) => BODY_CONTENT_GENERATE[col]({ deal }),
        }),
        {},
      ),
    [],
  );

  const columnFilterOptions = useMemo(
    () => ({
      stage: {
        options: stageFilterOption,
      },
      listing: {
        options: hasListingFilterOption,
      },
      ...(!isSingleElementArray(units)
        ? {
            ownerBusinessUnit: {
              options: businessUnitFilterOption,
            },
          }
        : {}),
    }),
    [
      stageFilterOption,
      hasListingFilterOption,
      units,
      businessUnitFilterOption,
    ],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const { data } = await api.enterpriseAnalytics.getAllDeals(
          enterprise?._id,
          {
            $page: page,
            $limit: limit,
            $sort: sort,
            stage,
            industry,
            colleagueIds,
            enterpriseBusinessUnitId,
            timeframe,
            hasListing,
          },
        );

        setPaginatedData(data);
        setIsEmpty?.(getIsArrayEmpty(data.items));
      } catch (e) {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    sort,
    colleagueIds,
    enterprise?._id,
    industry,
    page,
    stage,
    enterpriseBusinessUnitId,
    limit,
    timeframe,
    hasListing,
    setPaginatedData,
    setIsLoading,
    setIsEmpty,
  ]);

  return (
    <div className={classes}>
      <ActiveFilterSortPills
        filterOptions={filterOptions}
        sortOptions={translatedSortOptions}
        onChipLabelClick={onChipLabelClick}
        onSortChipLabelClick={onSortChipLabelClick}
      />

      <ReorderResizeTable
        className={classes}
        columns={columns}
        data={deals}
        totalPages={totalPages}
        totalItems={totalItems}
        recordName="deal"
        isDataLoading={isLoading}
        customSizes={CUSTOM_COL_SIZES}
        sortingValues={SORTING_VALUES}
        headingTranslations={HEADING_TRANSLATIONS}
        tableContentTransformations={renderedContent}
        columnFilterOptions={columnFilterOptions}
        onRowClick={({ _id }) => navigate(getSellingPortfolio(_id))}
        columnSizing={columnSizing}
        onColumnSizingChange={onColumnSizingChange}
        onReorder={onReorder as (columns: Array<keyof DealRoom>) => void}
      />
    </div>
  );
};

export default AnalyticsDeals;
